'use strict';

export default class PictureGridAds {
    constructor(root) {
        this.root = root;
        this.grid = root.querySelector('#picture-grid-container');
        this.itemCount = 0;
        this.usedGridAdIds = [];
        this.availableGridAdIds = this.detectAvailableGridAdIds();
        this.resizeHandlerBound = null;

        // Responsive insertion settings
        this.adSettings = {
            mobile: { after: 4, maxColumns: 1 },
            tablet: { after: 8, maxColumns: 2 },
            desktop: { after: 9, maxColumns: 3 },
            large: { after: 12, maxColumns: 4 }
        };
    }

    initialize() {
        if (!this.grid) {
            return;
        }

        this.observeGridChanges();
        this.bindResizeHandler();

        // Delayed checks and fixes for various SolAds loading speeds (scoped)
        setTimeout(() => this.applyGlobalHoverFix(), 2000);
        setTimeout(() => this.applyGlobalHoverFix(), 3000);
        setTimeout(() => this.applyGlobalHoverFix(), 6000);

        // Touch/hover behavior (scoped)
        this.initTouchDelegatedHover();
    }

    detectAvailableGridAdIds() {
        // Find SolAds containers 5+ (reserved for grid cloning)
        const ids = Array.from(document.querySelectorAll('div[id^="solads-advert-"]'))
            .map(el => {
                const m = el.id.match(/solads-advert-(\d+)/);
                return m ? parseInt(m[1], 10) : null;
            })
            .filter(n => typeof n === 'number' && n >= 5)
            .sort((a, b) => a - b);
        return ids.length ? ids : [5, 6, 7, 8, 9, 10];
    }

    getCurrentBreakpoint() {
        const width = window.innerWidth;
        if (width < 576) return 'mobile';
        if (width < 768) return 'tablet';
        if (width < 1200) return 'desktop';
        return 'large';
    }

    // Copy bootstrap column classes from first real item for visual parity
    getAdColumnClasses() {
        const sample = this.grid.querySelector('.item:not(.picture-native-ad)');
        if (sample) {
            const cols = Array.from(sample.classList).filter(c => c.startsWith('col-'));
            if (cols.length) return cols.join(' ');
        }
        // Fallback: typical pattern for 4/3/2/1 columns
        return 'col-lg-3 col-md-4 col-sm-6 col-12';
    }

    insertGridAd() {
        const breakpoint = this.getCurrentBreakpoint();
        const settings = this.adSettings[breakpoint];

        // Check whether an ad should be inserted
        if (this.itemCount < settings.after ||
            this.itemCount % settings.after !== 0 ||
            this.usedGridAdIds.length >= this.availableGridAdIds.length) {
            return;
        }

        const nextAdId = this.availableGridAdIds[this.usedGridAdIds.length];
        const adContainer = document.getElementById(`solads-advert-${nextAdId}`);
        if (!adContainer) return;

        // Create native ad container
        const nativeAdHtml = `
            <div class="item ${this.getAdColumnClasses()} mb-3 picture-native-ad" id="picture-ad-grid-${nextAdId}">
                <a href="#" class="picture-native-ad-link">
                    <div class="picture-native-ad-overlay">
                        <div class="picture-ad-label">AD</div>
                        <div class="picture-native-ad-content" id="solads-advert-${nextAdId}-copy">
                            <!-- SolAds content is copied here -->
                        </div>
                    </div>
                </a>
            </div>
        `;

        this.grid.insertAdjacentHTML('beforeend', nativeAdHtml);
        this.usedGridAdIds.push(nextAdId);

        // Content copying + hover fix (slightly delayed)
        setTimeout(() => this.copyAdContentAndApplyHover(nextAdId), 100);
    }

    copyAdContentAndApplyHover(adId) {
        const sourceContainer = document.getElementById(`solads-advert-${adId}`);
        const targetContainer = document.getElementById(`solads-advert-${adId}-copy`);
        if (!sourceContainer || !targetContainer) return;

        const sourceContent = sourceContainer.innerHTML.trim();
        if (sourceContent.length > 10) {
            // Copy content
            targetContainer.innerHTML = sourceContent;

            // Transfer links
            const adLinks = targetContainer.querySelectorAll('a');
            adLinks.forEach(link => {
                if (link.href && link.href !== '#') {
                    const parentLink = targetContainer.closest('.picture-native-ad-link');
                    if (parentLink) {
                        parentLink.href = link.href;
                        parentLink.target = link.target || '_blank';
                        parentLink.rel = 'nofollow';
                    }
                }
            });

            // Apply hover fix immediately
            this.applyHoverFixToAd(adId);
        } else {
            // Hide empty ads
            const adElement = document.getElementById(`picture-ad-grid-${adId}`);
            if (adElement) {
                adElement.style.display = 'none';
                adElement.setAttribute('data-empty', 'true');
            }
        }
    }

    // Apply hover overlay events to a specific ad (desktop only)
    applyHoverFixToAd(adId) {
        const adElement = document.getElementById(`picture-ad-grid-${adId}`);
        if (!adElement) return;

        const textOverlay = adElement.querySelector('.a .x');
        const parentLink = adElement.querySelector('.a');

        if (textOverlay && parentLink) {
            textOverlay.classList.add('solads-overlay-init');

            parentLink.addEventListener('mouseenter', () => {
                textOverlay.style.opacity = '1';
                textOverlay.style.transform = 'translateY(0)';
            });
            parentLink.addEventListener('mouseleave', () => {
                textOverlay.style.opacity = '0';
                textOverlay.style.transform = 'translateY(20px)';
            });
        }
    }

    observeGridChanges() {
        const observer = new MutationObserver(mutations => {
            let newItems = 0;
            mutations.forEach(mutation => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach(node => {
                        if (node.nodeType === 1 && node.classList && node.classList.contains('item') && !(node.id || '').startsWith('picture-ad-grid-')) {
                            newItems++;
                            this.itemCount++;
                        }
                    });
                }
            });

            if (newItems > 0) {
                this.insertGridAd();
            }
        });

        observer.observe(this.grid, { childList: true, subtree: false });
    }

    applyGlobalHoverFix() {
        const allTextOverlays = this.grid.querySelectorAll('.picture-native-ad .a .x');
        allTextOverlays.forEach(overlay => {
            const parentLink = overlay.closest('.a');
            if (parentLink && !parentLink.hasAttribute('data-hover-fixed')) {
                parentLink.setAttribute('data-hover-fixed', 'true');
                overlay.classList.add('solads-overlay-init');
            }
        });
    }

    bindResizeHandler() {
        this.resizeHandlerBound = () => {
            clearTimeout(this._resizeTimeout);
            this._resizeTimeout = setTimeout(() => {
                const adElements = this.grid.querySelectorAll('[id^="picture-ad-grid-"]');
                const cls = this.getAdColumnClasses();
                adElements.forEach(el => {
                    el.className = el.className.replace(/col-\w*-?\d*/g, 'item').replace(/\s+/g, ' ').trim();
                    el.className += ' ' + cls + ' mb-3 picture-native-ad';
                });
            }, 250);
        };
        window.addEventListener('resize', this.resizeHandlerBound);
    }

    initTouchDelegatedHover() {
        const isTouchEnv = () => window.matchMedia('(hover: none)').matches || window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
        if (!isTouchEnv()) return;

        const showOverlay = (root) => { if (!root) return; this.grid.querySelectorAll('.picture-native-ad.ad-hover').forEach(r => { if (r !== root) r.classList.remove('ad-hover'); }); root.classList.add('ad-hover'); };
        const hideOverlay = (root) => { if (!root) return; root.classList.remove('ad-hover'); };

        const prepareExisting = () => {
            this.grid.querySelectorAll('.picture-native-ad .a .x').forEach(ov => {
                const root = ov.closest('.picture-native-ad');
                if (root) { ov.classList.add('solads-overlay-init'); }
            });
        };
        prepareExisting(); [800, 2000, 4000].forEach(d => setTimeout(prepareExisting, d));

        // Pointerenter/leave scoped to grid
        this.grid.addEventListener('pointerenter', e => {
            if (e.pointerType === 'mouse') return;
            const tgt = e.target;
            if (!(tgt instanceof Element) || typeof tgt.closest !== 'function') return;
            const root = tgt.closest('.picture-native-ad'); if (root) showOverlay(root);
        }, true);
        this.grid.addEventListener('pointerleave', e => {
            if (e.pointerType === 'mouse') return;
            const tgt = e.target;
            if (!(tgt instanceof Element) || typeof tgt.closest !== 'function') return;
            const root = tgt.closest('.picture-native-ad'); if (root) hideOverlay(root);
        }, true);

        // Touchmove: use document.elementFromPoint but keep actions scoped to grid
        let rafScheduled = false;
        document.addEventListener('touchmove', e => {
            if (!e.touches || !e.touches.length) return; if (rafScheduled) return; rafScheduled = true;
            requestAnimationFrame(() => {
                rafScheduled = false;
                const t = e.touches[0];
                const el = document.elementFromPoint(t.clientX, t.clientY);
                const root = el && (el.closest ? el.closest('.picture-native-ad') : null);
                if (root && this.grid.contains(root)) {
                    showOverlay(root);
                } else {
                    this.grid.querySelectorAll('.picture-native-ad.ad-hover').forEach(hideOverlay);
                }
            });
        }, { passive: true });

        // Touchstart: scoped to grid
        this.grid.addEventListener('touchstart', e => {
            const tgt = e.target;
            if (!(tgt instanceof Element) || typeof tgt.closest !== 'function') return;
            const root = tgt.closest('.picture-native-ad'); if (root) showOverlay(root);
        }, { passive: true });

        // Touchend: hide after short delay
        this.grid.addEventListener('touchend', () => {
            const open = this.grid.querySelector('.picture-native-ad.ad-hover');
            if (open) {
                setTimeout(() => { if (!this.grid.querySelector('.picture-native-ad.ad-hover[data-lock]')) hideOverlay(open); }, 600);
            }
        });

        // First/second tap navigation logic scoped to grid
        this.grid.addEventListener('click', e => {
            const tgt = e.target;
            if (!(tgt instanceof Element) || typeof tgt.closest !== 'function') return;
            const root = tgt.closest('.picture-native-ad');
            if (!root) return; // only inside our grid ads

            if (!root.dataset.firstTap) {
                e.preventDefault(); e.stopPropagation();
                root.dataset.firstTap = Date.now();
                showOverlay(root);
            } else {
                const elapsed = Date.now() - parseInt(root.dataset.firstTap, 10);
                if (elapsed < 800) {
                    delete root.dataset.firstTap; // allow navigation
                } else {
                    e.preventDefault(); e.stopPropagation();
                    root.dataset.firstTap = Date.now();
                    showOverlay(root);
                }
            }
        }, true);

        // Watch for dynamically added overlays inside grid
        const mo = new MutationObserver(muts => {
            let need = false;
            muts.forEach(m => m.addedNodes.forEach(n => {
                if (n.nodeType === 1 && (n.matches?.('.picture-native-ad') || n.querySelector?.('.picture-native-ad .a .x'))) need = true;
            }));
            if (need) setTimeout(prepareExisting, 60);
        });
        mo.observe(this.grid, { childList: true, subtree: true });
    }
}
